/**
 * Reusable comment Popover for table cells. Shared by ReportsList (Notes
 * column on a report) and ActionItemsInbox (💬 chip on an A#).
 *
 * Trigger renders the existing count as a Badge link; click reveals the
 * popover with the comment thread + an inline "Add note" textarea. Posting
 * a comment refreshes the popover and notifies the parent so the count
 * chip updates on the next list refetch.
 */
import { useCallback, useState } from "react";
import { Badge, Box, Button, Popover, SpaceBetween, Textarea } from "@cloudscape-design/components";
import type { Comment } from "@/core/api/types";
import { addComment, deleteComment, editComment, listComments } from "@/core/api/comments";
import { formatET } from "@/core/ui/time";

interface Props {
  kind: "report" | "action-item";
  targetId: string;
  targetLabel: string;
  count: number;
  onChange: () => void;
}

export function CommentsCell({ kind, targetId, targetLabel, count, onChange }: Props) {
  const [items, setItems] = useState<Comment[] | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const reload = useCallback(async () => {
    setItems(await listComments(kind, targetId));
  }, [kind, targetId]);

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await addComment(kind, targetId, body);
      setDraft("");
      await reload();
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (c: Comment) => {
    setEditingId(c.id);
    setEditDraft(c.body);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
  };
  const saveEdit = async (commentId: string) => {
    const body = editDraft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await editComment(kind, commentId, body);
      cancelEdit();
      await reload();
      onChange();
    } finally {
      setBusy(false);
    }
  };
  const remove = async (commentId: string) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    setBusy(true);
    try {
      await deleteComment(kind, commentId);
      await reload();
      onChange();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover
      size="large"
      triggerType="custom"
      header={`Notes — ${targetLabel}`}
      content={
        <div style={{ minWidth: 420 }}>
          <SpaceBetween size="m">
            {items === null && <Box color="text-body-secondary">Loading…</Box>}
            {items?.length === 0 && <Box color="text-body-secondary">No notes yet.</Box>}
            {items?.map((c, i) => {
              const mine = !!c.editable; // #96: server-provided ownership flag
              const isLast = i >= (items?.length ?? 0) - 1;
              return (
                <div
                  key={c.id}
                  style={{
                    paddingBottom: 12,
                    borderBottom: isLast ? "none" : "1px solid var(--color-border-divider-default, #e9ebed)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <Box variant="small" color="text-body-secondary">
                      <strong>{c.user_email ?? "user"}</strong> · {formatET(c.created_at)}
                    </Box>
                    {mine && editingId !== c.id && (
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="inline-link" onClick={() => startEdit(c)} disabled={busy}>
                          Edit
                        </Button>
                        <Button variant="inline-link" onClick={() => void remove(c.id)} disabled={busy}>
                          Delete
                        </Button>
                      </SpaceBetween>
                    )}
                  </div>
                  {editingId === c.id ? (
                    <Box padding={{ top: "xs" }}>
                      <SpaceBetween size="xs">
                        <Textarea
                          value={editDraft}
                          onChange={({ detail }) => setEditDraft(detail.value)}
                          rows={3}
                          disabled={busy}
                        />
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            variant="primary"
                            disabled={!editDraft.trim() || busy}
                            loading={busy}
                            onClick={() => void saveEdit(c.id)}
                          >
                            Save
                          </Button>
                          <Button variant="link" onClick={cancelEdit} disabled={busy}>
                            Cancel
                          </Button>
                        </SpaceBetween>
                      </SpaceBetween>
                    </Box>
                  ) : (
                    <Box padding={{ top: "xxs" }}>
                      <span style={{ whiteSpace: "pre-wrap" }}>{c.body}</span>
                    </Box>
                  )}
                </div>
              );
            })}
            <Textarea
              value={draft}
              onChange={({ detail }) => setDraft(detail.value)}
              rows={3}
              placeholder="Add a note…"
              disabled={busy}
            />
            <Box float="right">
              <Button variant="primary" disabled={!draft.trim() || busy} loading={busy} onClick={submit}>
                Add note
              </Button>
            </Box>
          </SpaceBetween>
        </div>
      }
    >
      <Button variant="inline-link" onClick={() => void reload()} ariaLabel={`Open notes for ${targetLabel}`}>
        {count > 0 ? <Badge color="grey">💬 {count}</Badge> : "💬"}
      </Button>
    </Popover>
  );
}
